import React, { useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { motion, AnimatePresence } from 'motion/react';
import { Users, Settings, Plus, TrendingUp, Home, CheckCircle2, Zap, BarChart3 } from 'lucide-react';
import HomeHeader from './HomeHeader';
import HomeKPIs from './HomeKPIs';
import { resolveDashboard } from '../../core/business-profile/businessProfileResolver';
import type { BusinessProfile } from '../../core/business-profile/businessProfile';

/**
 * CYZOR HOME / Dashboard
 * 
 * Propósito: Visão executiva/operacional do Workspace
 * 
 * NÃO é:
 * - Marketplace
 * - App Store
 * - Tela de aquisição de módulos
 * - Tela de seleção de apps
 * 
 * É:
 * - Visão geral da operação
 * - Indicadores do workspace
 * - Atividades recentes
 * - Acesso aos apps via sidebar (não aqui)
 */
export function CyzorHome() {
  const { user, activeWorkspace } = useAuth();
  const { setCurrentView } = useUI();

  // Extrair apps habilitados do workspace (do onboarding)
  const enabledApps = useMemo(() => {
    const apps = activeWorkspace?.settings?.enabledApps || [];
    return Array.isArray(apps) ? apps : [];
  }, [activeWorkspace?.settings?.enabledApps]);

  // Extrair objetivos selecionados do workspace
  const selectedObjectives = useMemo(() => {
    const objectives = activeWorkspace?.settings?.selectedObjectives || [];
    return Array.isArray(objectives) ? objectives : [];
  }, [activeWorkspace?.settings?.selectedObjectives]);

  const dashboardConfig = useMemo(() => {
    const profile = activeWorkspace?.settings?.businessProfile as BusinessProfile | undefined;
    return profile ? resolveDashboard(profile) : null;
  }, [activeWorkspace?.settings?.businessProfile]);

  // Verificar se é primeiro acesso (workspace recém-criado)
  const isNewWorkspace = useMemo(() => {
    const onboardingCompleted = activeWorkspace?.settings?.onboardingCompleted;
    const onboardingCompletedAt = activeWorkspace?.settings?.onboardingCompletedAt;
    
    if (!onboardingCompleted) return true;
    
    // Se foi completado há menos de 1 minuto, considerar novo
    if (onboardingCompletedAt) {
      const completedTime = new Date(onboardingCompletedAt).getTime();
      const now = new Date().getTime();
      const diffMs = now - completedTime;
      const diffMinutes = diffMs / (1000 * 60);
      return diffMinutes < 1;
    }
    
    return false;
  }, [activeWorkspace?.settings?.onboardingCompletedAt, activeWorkspace?.settings?.onboardingCompleted]);

  const handleSettings = () => setCurrentView('configuracoes');
  const handleWorkspaceSettings = () => setCurrentView('equipe');
  const handleHome = () => setCurrentView('dashboard');

  return (
    <div id="cyzor-home-main" className="w-full mx-auto pb-12 animate-in fade-in duration-500 relative">
      {/* Ambient dark background that covers the top section */}
      <div className="absolute top-0 left-0 w-full h-[280px] sm:h-[300px] bg-[#0F172A] rounded-b-[40px] z-0 overflow-hidden shadow-[0_20px_40px_-15px_rgba(0,0,0,0.1)]">
        <div className="absolute -top-20 left-1/4 w-[500px] h-[500px] bg-indigo-500/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-10 right-1/4 w-[400px] h-[400px] bg-blue-500/20 rounded-full blur-[100px] pointer-events-none" />
      </div>

      <div className="flex flex-col gap-10 px-4 sm:px-6 lg:px-8 pt-6 relative z-10">
        {/* Header Section */}
        <HomeHeader />

        {/* Welcome / New Workspace Section */}
        <AnimatePresence>
          {isNewWorkspace && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-200/50 rounded-[24px] p-6 sm:p-7 backdrop-blur-sm"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={24} className="text-blue-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-blue-900 mb-1">Seu Workspace está pronto! 🎉</h2>
                  <p className="text-sm text-blue-700 mb-3">
                    Comece organizando sua operação usando os aplicativos disponíveis na navegação lateral.
                  </p>
                  {enabledApps.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-blue-600 font-medium">
                      <Zap size={14} className="fill-blue-600" />
                      Você selecionou {enabledApps.length} aplicativo{enabledApps.length > 1 ? 's' : ''} para começar
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Analytics and Metrics Grid */}
        <div className="grid grid-cols-12 gap-6 w-full">
          {/* KPI Grid (Full Width) */}
          <div className="col-span-12">
            <HomeKPIs 
              dashboardData={null}
              projects={[]}
              finance={[]}
              companies={[]}
              setCurrentView={setCurrentView}
              isLoading={false}
            />
          </div>

          {/* Main Content Area (8 columns) */}
          <div className="col-span-12 lg:col-span-8 flex flex-col gap-6">
            {/* Workspace Overview Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-[#0F172A0F] rounded-[24px] p-6 sm:p-7 shadow-[0_1px_3px_rgba(15,23,42,0.03)]"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600">
                  <Home size={20} />
                </div>
                <h3 className="text-lg font-bold text-[#111111]">{dashboardConfig?.title || 'Visão Geral da Workspace'}</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-4 bg-slate-50/50 rounded-xl">
                  <span className="text-xs font-bold text-slate-500 uppercase block mb-2">Workspace</span>
                  <p className="text-xl font-bold text-[#111111] mb-1">{activeWorkspace?.name}</p>
                  <span className="text-xs text-slate-500 font-medium">
                    Plano {activeWorkspace?.plan || 'Padrão'}
                  </span>
                </div>
                <div className="p-4 bg-blue-50/50 rounded-xl">
                  <span className="text-xs font-bold text-blue-600 uppercase block mb-2">Membros</span>
                  <p className="text-xl font-bold text-blue-900">{activeWorkspace?.membersCount || 1}</p>
                  <button
                    onClick={handleWorkspaceSettings}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-2"
                  >
                    Gerenciar →
                  </button>
                </div>
                <div className="p-4 bg-indigo-50/50 rounded-xl">
                  <span className="text-xs font-bold text-indigo-600 uppercase block mb-2">Apps Ativos</span>
                  <p className="text-xl font-bold text-indigo-900">{enabledApps.length}</p>
                    <p className="text-xs text-indigo-600 font-medium mt-2">
                    {dashboardConfig ? `${dashboardConfig.priorityMetrics.length} prioridades definidas` : enabledApps.length > 0 ? 'Configurados no onboarding' : 'Perfil em configuração'}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white border border-[#0F172A0F] rounded-[24px] p-6 sm:p-7 shadow-[0_1px_3px_rgba(15,23,42,0.03)]"
            >
              <h3 className="text-lg font-bold text-[#111111] mb-5 flex items-center gap-2">
                <TrendingUp size={20} className="text-blue-600" />
                Ações Rápidas
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  onClick={handleWorkspaceSettings}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-200 transition-all group"
                >
                  <Users size={20} className="text-slate-600 group-hover:text-blue-600" />
                  <span className="text-xs font-bold text-slate-600 group-hover:text-blue-600">Equipe</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  onClick={handleSettings}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 transition-all group"
                >
                  <Settings size={20} className="text-slate-600 group-hover:text-indigo-600" />
                  <span className="text-xs font-bold text-slate-600 group-hover:text-indigo-600">Configurações</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-slate-50 hover:bg-amber-50 border border-slate-200 hover:border-amber-200 transition-all group"
                >
                  <Plus size={20} className="text-slate-600 group-hover:text-amber-600" />
                  <span className="text-xs font-bold text-slate-600 group-hover:text-amber-600">Novo Item</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-slate-50 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-200 transition-all group"
                >
                  <BarChart3 size={20} className="text-slate-600 group-hover:text-emerald-600" />
                  <span className="text-xs font-bold text-slate-600 group-hover:text-emerald-600">Métricas</span>
                </motion.button>
              </div>
            </motion.div>
          </div>

          {/* Secondary Widgets Sidebar (4 columns) */}
          <div className="col-span-12 lg:col-span-4 flex flex-col gap-6">
            {/* Seus Aplicativos */}
            {enabledApps.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white border border-[#0F172A0F] rounded-[24px] p-6 sm:p-7 shadow-[0_1px_3px_rgba(15,23,42,0.03)]"
              >
                <h3 className="text-lg font-bold text-[#111111] mb-5">Seus Aplicativos</h3>
                <div className="flex flex-col gap-3">
                  {enabledApps.map((app, idx) => (
                    <motion.button
                      key={idx}
                      whileHover={{ x: 4 }}
                      className="text-left p-3 rounded-lg bg-slate-50 hover:bg-blue-50 border border-slate-100 hover:border-blue-200 transition-all group"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-slate-700 group-hover:text-blue-700">
                          {typeof app === 'string' ? app.charAt(0).toUpperCase() + app.slice(1) : app}
                        </span>
                        <TrendingUp size={14} className="text-slate-400 group-hover:text-blue-600" />
                      </div>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Recent Activity / Empty State */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white border border-[#0F172A0F] rounded-[24px] p-6 sm:p-7 shadow-[0_1px_3px_rgba(15,23,42,0.03)] flex-1 flex flex-col items-center justify-center min-h-[300px]"
            >
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Zap size={24} className="text-slate-400" />
                </div>
                <h3 className="text-lg font-bold text-slate-700 mb-2">Atividade Recente</h3>
                <p className="text-sm text-slate-500">Nenhuma atividade registrada ainda</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { CyzorHome as default };
